/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { Icon, Alert, Text, Button, Heading } from 'sarsaparilla';
import { v4 as uuid } from 'uuid';

import TripDestinationItem from './TripDestinationItem';
import TripDestinationHeaderItem from './TripDestinationHeaderItem';
import { isTripValid } from '../utils/tripUtils';
import { scrollTo } from '../utils/uiUtils';

const propTypes = {
    trip: PropTypes.object,
    showAddDestinationButton: PropTypes.bool,
    showErrors: PropTypes.bool,
    errorMessage: PropTypes.string,
    onChange: PropTypes.func,
    activeDestinationIndex: PropTypes.number,
    showClearButton: PropTypes.bool,
    onClear: PropTypes.func,
    headingLevel: PropTypes.number,
};

const defaultProps = {
    showAddDestinationButton: true,
    showClearButton: false,
    headingLevel: 3,
};

export default class TripDestinationsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            trip: props.trip,
        };
        this.addDestination = this.addDestination.bind(this);
        this.onDestinationMove = this.onDestinationMove.bind(this);
        this.onDestinationDelete = this.onDestinationDelete.bind(this);
        this.onDestinationChange = this.onDestinationChange.bind(this);
        this.onTripChange = this.onTripChange.bind(this);
    }

    componentDidMount() {
        if (isEmpty(this.state.trip.destinations)) {
            this.addDestination();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (JSON.stringify(this.state.trip) !== JSON.stringify(nextProps.trip)) {
            this.setState({
                trip: nextProps.trip,
            });
        }
        if (
            nextProps.activeDestinationIndex &&
            nextProps.activeDestinationIndex !== this.state.activeDestinationIndex
        ) {
            // scroll itinerary to specific date on change
            if (this._destinationsRef) {
                this.setState({
                    activeDestinationIndex: nextProps.activeDestinationIndex,
                });
                this.scrollToDestinationIndex(nextProps.activeDestinationIndex);
            }
        }
    }

    onDestinationMove(fromIndex, toIndex) {
        /**
         * Directly modifying the state object `trip` and then updating it with
         * `this.setState` is an anti-pattern.
         * This approach mutates the state directly which can lead to unexpected
         * behavior and bugs.
         * TODO: Create a new copy of the trip object and update it immutably.
         */
        const trip = this.state.trip;
        const to = this.props.trip.destinations[toIndex];
        trip.destinations[toIndex] = trip.destinations[fromIndex];
        trip.destinations[fromIndex] = to;
        this.setState({ trip });
        if (this.props.onChange) {
            this.props.onChange(trip);
        }
    }

    onDestinationDelete(index) {
        if (index >= 0 && index < this.state.trip.destinations.length) {
            const trip = this.state.trip;
            trip.destinations.splice(index, 1);
            this.setState({ trip });
            if (this.props.onChange) {
                this.props.onChange(trip);
            }
        }
    }

    onDestinationChange(destination, destinationIndex) {
        const trip = { ...this.state.trip };
        trip.destinations[destinationIndex] = destination;
        this.setState({ trip });
        if (this.props.onChange) {
            this.props.onChange(trip);
        }
    }

    onTripChange(trip) {
        this.setState({ trip });
        if (this.props.onChange) {
            this.props.onChange(trip);
        }
    }

    addDestination() {
        const trip = this.state.trip;
        const destination = {
            id: uuid(),
            name: '',
            lat: null,
            lng: null,
            entity_id: null,
            entity_type: null,
            items: [],
        };
        if (trip.destinations.length > 0) {
            destination.duration = 1;
        }
        trip.destinations[trip.destinations.length] = destination;
        this.setState({ trip });
        if (this.props.onChange) {
            this.props.onChange(trip);
        }
    }

    scrollToDestinationIndex(index) {
        let refIndex = index;
        if (refIndex > this._destinationsRef.children.length - 1) {
            refIndex = this._destinationsRef.children.length - 1;
        }

        const to =
            this._destinationsRef.children[refIndex].offsetTop -
            this._destinationsRef.offsetTop +
            300;
        scrollTo(this._destinationsRef, to, 100);
    }

    isValid() {
        return isTripValid(this.state.trip);
    }

    render() {
        return (
            <div className="trip-planner-destinations-form">
                <div className="trip-planner-destinations-header">
                    <Heading
                        appearance="h5"
                        headingLevel={this.props.headingLevel}
                        className="trip-planner-destinations-form-header"
                        headingAlign="center"
                    >
                        When and where are you going?
                    </Heading>
                    {this.props.showClearButton && (
                        <div>
                            <Button
                                appearance="link"
                                className="rec-button-link"
                                onClick={this.props.onClear}
                            >
                                Clear Itinerary
                            </Button>
                        </div>
                    )}
                </div>

                <div
                    className="trip-planner-destinations-form-content"
                    ref={(ref) => (this._destinationsRef = ref)}
                >
                    {this.props.showErrors &&
                        (!this.isValid() || this.props.errorMessage) && (
                            <Alert
                                hasAriaLive
                                className="px-2 pt-2"
                                type="error"
                                shouldFocusOnMount
                            >
                                Please select valid locations for all your destinations
                                highlighted below
                            </Alert>
                        )}
                    <Text size="sm" className="trip-planner-title">
                        Enter your dates
                    </Text>
                    <Text size="sm" className="trip-planner-title">
                        How many people
                    </Text>

                    <TripDestinationHeaderItem
                        trip={this.state.trip}
                        onChange={this.onTripChange}
                    />

                    <Text size="sm" className="trip-planner-title">
                        Enter your destinations
                    </Text>
                    <Text size="sm" className="trip-planner-title">
                        How many days
                    </Text>

                    {this.state.trip.destinations.map((destination, index) => {
                        return (
                            <TripDestinationItem
                                key={destination.id}
                                destination={destination}
                                destinationIndex={index}
                                trip={this.state.trip}
                                onMove={this.onDestinationMove}
                                onDelete={this.onDestinationDelete}
                                onChange={this.onDestinationChange}
                                showErrors={this.props.showErrors}
                            />
                        );
                    })}

                    {this.props.showAddDestinationButton && (
                        <div className="trip-planner-add-destination-wrapper">
                            <Button
                                className="rec-button-link"
                                appearance="link"
                                onClick={this.addDestination}
                                iconBeforeElement={<Icon iconName="add-circle-outline" />}
                            >
                                Add Destination
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

TripDestinationsForm.propTypes = propTypes;
TripDestinationsForm.defaultProps = defaultProps;
